import React, { useState } from "react";
import { Col, Card, Form, Button, Spinner } from "react-bootstrap";
import { Formik } from "formik";
import * as Yup from "yup";
import { showSuccessToast } from "utils/toast";
import { useDispatch } from "react-redux";
import {
  addHoliday as addHolidayInStore,
  updateHoliday as updateHolidayInStore,
  deleteHoliday as deleteHolidayFromStore,
} from "state/slices/holiday/holidaySlice";
import {
  useCreateHolidayMutation,
  useUpdateHolidayMutation,
  useDeleteHolidayMutation,
} from "state/slices/holiday/holidayApiSlice";

const initialValues = {
  id: null,
  name: "",
  date: "",
};

const GeneralAccessManagement = React.memo(
  ({
    isAllowedToRead,
    isAllowedToCreate,
    isAllowedToUpdate,
    isAllowedToDelete,
    HolidayViewManagement,
  }) => {
    const [selectedRow, setSelectedRow] = useState(null);
    const [formikValues, setFormikValues] = useState(initialValues);

    const dispatch = useDispatch();

    const [createHoliday] = useCreateHolidayMutation();
    const [updateHoliday] = useUpdateHolidayMutation();
    const [deleteHoliday] = useDeleteHolidayMutation();

    const handleCheckboxChange = (event, row) => {
      if (event.target.checked) {
        setSelectedRow({ ...row });
      } else {
        setSelectedRow(null);
        setFormikValues(initialValues);
      }
    };

    const handleEdit = () => {
      setFormikValues(selectedRow);
    };

    const addHolidayManage = async (holidayData) => {
      try {
        const data = await createHoliday(holidayData).unwrap();
        dispatch(addHolidayInStore(data));
        showSuccessToast("Holiday added successfully");
      } catch {}
    };

    const updateHolidayManage = async (dataToUpdate) => {
      try {
        const data = await updateHoliday(dataToUpdate).unwrap();
        dispatch(updateHolidayInStore(data.holiday));
        showSuccessToast("Holiday updated successfully");
      } catch {}
    };

    const deleteHolidayManage = async () => {
      try {
        const rowId = selectedRow.id;
        await deleteHoliday(rowId).unwrap();
        dispatch(deleteHolidayFromStore(rowId));
        showSuccessToast("Holiday deleted successfully");
      } catch {}
      setFormikValues(initialValues);
      setSelectedRow(null);
    };

    const handleSubmit = async (values, { resetForm }) => {
      try {
        if (values.id) {
          await updateHolidayManage(values);
        } else {
          await addHolidayManage(values);
        }
        setSelectedRow(null);
        setFormikValues(initialValues);
        resetForm();
      } catch {}
    };

    const validationSchema = Yup.object().shape({
      name: Yup.string().required("Holiday name is required"),
      date: Yup.string().required("Date is required"),
    });

    return (
      <section className="section dashboard">
        {(isAllowedToCreate || isAllowedToUpdate) && (
          <Card>
            <Card.Body>
              <Formik
                enableReinitialize={true}
                initialValues={formikValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                {({ values, handleChange, handleSubmit, touched, errors, isSubmitting }) => (
                  <Form noValidate onSubmit={handleSubmit} className="row mt-3">
                    <Col md={5} className="mb-3 mb-md-0">
                      <Form.Group controlId="name">
                        <Form.Control
                          type="text"
                          name="name"
                          placeholder="Enter holiday name"
                          value={values.name}
                          onChange={handleChange}
                          isInvalid={touched.name && !!errors.name}
                          disabled={isSubmitting}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.name}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={4} className="mb-3 mb-md-0">
                      <Form.Group controlId="date">
                        <Form.Control
                          type="date"
                          name="date"
                          value={values.date}
                          onChange={handleChange}
                          isInvalid={touched.date && !!errors.date}
                          disabled={isSubmitting}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.date}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Button variant="primary" className="w-100" type="submit" disabled={isSubmitting}>
                        {isSubmitting ? (
                          <>
                            <Spinner
                              animation="border"
                              size="sm"
                              role="status"
                              className="me-2"
                              style={{ width: "18px", height: "18px" }}
                            />
                            {formikValues.id ? "Updating..." : "Adding..."}
                          </>
                        ) : (
                          formikValues.id ? "Save" : "Add"
                        )}
                      </Button>
                    </Col>
                  </Form>
                )}
              </Formik>
            </Card.Body>
          </Card>
        )}
        {isAllowedToRead && (
          <HolidayViewManagement
            isAllowedToUpdate={isAllowedToUpdate}
            isAllowedToDelete={isAllowedToDelete}
            handleCheckboxChange={handleCheckboxChange}
            handleEdit={handleEdit}
            deleteHolidayManage={deleteHolidayManage}
            selectedRow={selectedRow}
          />
        )}
      </section>
    );
  }
);

export default GeneralAccessManagement;
